import { FormEvent, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  ADMIN_NOTICES,
  CREATE_NOTICE,
  UPDATE_NOTICE,
  DELETE_NOTICE,
} from "../../../api/boss-notice";
import { notify, notifyError } from "../../../utils/toast";
import { getErrorMessage } from "../../../utils/error";

type NoticeItem = {
  id: number;
  title: string;
  pageUrl: string | null;
  note: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
};

const AdminBossNotice = () => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formPageUrl, setFormPageUrl] = useState("");
  const [formNote, setFormNote] = useState("");

  const { data, loading, refetch } = useQuery<{ adminNotices: NoticeItem[] }>(ADMIN_NOTICES);

  const [createNotice, { loading: creating }] = useMutation<
    { createNotice: NoticeItem },
    { input: { title: string; pageUrl?: string; note?: string } }
  >(CREATE_NOTICE, {
    onCompleted: () => {
      notify("공지가 등록되었습니다.");
      setIsAddOpen(false);
      setFormTitle("");
      setFormPageUrl("");
      setFormNote("");
      refetch();
    },
    onError: (error) => {
      notifyError(`등록 실패: ${getErrorMessage(error)}`);
    },
  });

  const [updateNotice, { loading: updating }] = useMutation<
    { updateNotice: NoticeItem },
    { input: { id: number; title?: string; pageUrl?: string; note?: string } }
  >(UPDATE_NOTICE, {
    onCompleted: () => {
      notify("공지가 수정되었습니다.");
      setEditingId(null);
      refetch();
    },
    onError: (error) => {
      notifyError(`수정 실패: ${getErrorMessage(error)}`);
    },
  });

  const [deleteNotice, { loading: deleting }] = useMutation<
    { deleteNotice: { ok: boolean; error: string | null } },
    { id: number }
  >(DELETE_NOTICE, {
    onCompleted: (res) => {
      if (res.deleteNotice.ok) {
        notify("공지가 삭제되었습니다.");
        refetch();
      } else {
        notifyError(res.deleteNotice.error ?? "삭제에 실패했습니다.");
      }
    },
    onError: (error) => {
      notifyError(`삭제 실패: ${getErrorMessage(error)}`);
    },
  });

  const notices: NoticeItem[] = data?.adminNotices ?? [];

  const handleSubmitAdd = (e: FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      notifyError("제목을 입력해주세요.");
      return;
    }
    createNotice({
      variables: {
        input: {
          title: formTitle.trim(),
          pageUrl: formPageUrl.trim() || undefined,
          note: formNote.trim() || undefined,
        },
      },
    });
  };

  const handleSubmitEdit = (e: FormEvent, id: number) => {
    e.preventDefault();
    const title = (e.currentTarget.querySelector("[name=editTitle]") as HTMLInputElement)?.value?.trim();
    const pageUrl = (e.currentTarget.querySelector("[name=editPageUrl]") as HTMLInputElement)?.value?.trim();
    const note = (e.currentTarget.querySelector("[name=editNote]") as HTMLInputElement)?.value?.trim();
    if (!title) {
      notifyError("제목을 입력해주세요.");
      return;
    }
    updateNotice({
      variables: {
        input: {
          id,
          title,
          pageUrl: pageUrl || undefined,
          note: note || undefined,
        },
      },
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm("이 공지를 삭제(비표시) 하시겠습니까? 사장님 앱에는 더 이상 보이지 않습니다.")) return;
    deleteNotice({ variables: { id } });
  };

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl font-bold mb-6">사장님 공지 관리</h1>
      <p className="text-sm text-gray-600 mb-6">
        여기서 등록한 공지는 사장님 앱 &gt; 내 매장 &gt; 공지사항에서 확인할 수 있습니다. 페이지 URL에는{" "}
        <a
          href="https://www.notion.so/1ab08e6e11b380419c29ce363dd0269a?source=copy_link"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          노션 페이지
        </a>
        의 URL을 넣어주세요. 클릭 시 해당 노션 페이지가 열립니다.
      </p>

      <div className="mb-6">
        <button
          type="button"
          onClick={() => setIsAddOpen(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 font-medium"
        >
          + 공지 추가
        </button>
      </div>

      {isAddOpen && (
        <form
          onSubmit={handleSubmitAdd}
          className="mb-8 p-6 border border-gray-200 rounded-xl bg-gray-50"
        >
          <h2 className="text-lg font-semibold mb-4">새 공지 등록</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제목 (필수)
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="예: 2월 정산 일정 안내"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                노션 URL (선택)
              </label>
              <input
                type="url"
                value={formPageUrl}
                onChange={(e) => setFormPageUrl(e.target.value)}
                placeholder="https://www.notion.so/..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비고 (선택, 어드민용)
              </label>
              <input
                type="text"
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
                placeholder="내부 메모"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={creating || !formTitle.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
              {creating ? "등록 중..." : "등록"}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAddOpen(false);
                setFormTitle("");
                setFormPageUrl("");
                setFormNote("");
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
            >
              취소
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-gray-500">로딩 중...</div>
      ) : notices.length === 0 ? (
        <div className="text-gray-500">등록된 공지가 없습니다.</div>
      ) : (
        <ul className="space-y-4">
          {notices.map((notice) => (
            <li
              key={notice.id}
              className={`border rounded-xl p-4 bg-white ${
                notice.isDeleted ? "opacity-60 bg-gray-50" : ""
              }`}
            >
              {editingId === notice.id ? (
                <form
                  onSubmit={(e) => handleSubmitEdit(e, notice.id)}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      제목
                    </label>
                    <input
                      name="editTitle"
                      defaultValue={notice.title}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      노션 URL
                    </label>
                    <input
                      name="editPageUrl"
                      defaultValue={notice.pageUrl ?? ""}
                      placeholder="https://www.notion.so/..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비고
                    </label>
                    <input
                      name="editNote"
                      defaultValue={notice.note ?? ""}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
                    >
                      {updating ? "저장 중..." : "저장"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      취소
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                          {notice.title}
                        </span>
                        {notice.isDeleted && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-300 text-gray-600">
                            삭제됨
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {new Date(notice.createdAt).toLocaleDateString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      {notice.pageUrl && (
                        <a
                          href={notice.pageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline mt-1 inline-block break-all"
                        >
                          {notice.pageUrl}
                        </a>
                      )}
                      {notice.note && (
                        <p className="text-sm text-gray-500 mt-1">비고: {notice.note}</p>
                      )}
                    </div>
                    {!notice.isDeleted && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingId(notice.id)}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(notice.id)}
                          disabled={deleting}
                          className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminBossNotice;
